import * as signalR from '@microsoft/signalr';
import { API_ORIGIN } from '../utils/apiBase';

/**
 * SignalR connection wrapper for the chat hub. Single connection per page —
 * the hooks below subscribe and unsubscribe from the shared instance so
 * mounting two threads doesn't open two WebSockets.
 *
 * Falls back to polling silently if the connection can't be established —
 * the rest of the chat UI works on REST regardless. We just lose live
 * push, which the polling layer in MessagesPage covers.
 */

let connection = null;
let connectPromise = null;
const messageHandlers = new Set();
const threadBumpHandlers = new Set();
const typingHandlers = new Set();
const threadReadHandlers = new Set();
// Plan detection: a private suggestion for THIS viewer (sent to the user group, never the thread's).
const planHandlers = new Set();
// Calls (Oct 2026): invites and signals ride the same socket, on the user channel.
const callInviteHandlers = new Set();
const callSignalHandlers = new Set();
const joinedThreads = new Set();
// Per-thread last-sent timestamp, so we don't flood the hub with one
// invocation per keystroke. Throttled to one ping per 2 seconds.
const lastTypingSentAt = new Map();
const TYPING_THROTTLE_MS = 2000;

function hubUrl() {
  // The API origin (no /api) + the hub path — same host the REST calls use.
  return `${API_ORIGIN}/hubs/messages`;
}

async function ensureConnection() {
  if (connection && connection.state === signalR.HubConnectionState.Connected) {
    return connection;
  }
  if (connectPromise) return connectPromise;

  connectPromise = (async () => {
    if (!connection) {
      connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl(), {
          accessTokenFactory: () => localStorage.getItem('accessToken') || '',
          transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
          skipNegotiation: false,
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(signalR.LogLevel.Warning)
        .build();

      connection.on('messageReceived', (msg) => {
        for (const h of messageHandlers) {
          try { h(msg); } catch (e) { console.error('messageReceived handler', e); }
        }
      });
      connection.on('threadBumped', (info) => {
        for (const h of threadBumpHandlers) {
          try { h(info); } catch (e) { console.error('threadBumped handler', e); }
        }
      });

      connection.on('typing', (info) => {
        for (const h of typingHandlers) {
          try { h(info); } catch (e) { console.error('typing handler', e); }
        }
      });
      // Seen receipts: {threadId, userId, at} whenever a member opens the thread.
      connection.on('threadRead', (info) => {
        for (const h of threadReadHandlers) {
          try { h(info); } catch (e) { console.error('threadRead handler', e); }
        }
      });

      connection.on('planSuggested', (plan) => {
        for (const h of planHandlers) {
          try { h(plan); } catch (e) { console.error('planSuggested handler', e); }
        }
      });
      connection.on('callInvite', (inv) => {
        for (const h of callInviteHandlers) {
          try { h(inv); } catch (e) { console.error('callInvite handler', e); }
        }
      });
      connection.on('callSignal', (sig) => {
        for (const h of callSignalHandlers) {
          try { h(sig); } catch (e) { console.error('callSignal handler', e); }
        }
      });

      connection.onreconnected(async () => {
        // Re-join all groups we cared about pre-disconnect.
        const stillJoined = Array.from(joinedThreads);
        joinedThreads.clear();
        for (const id of stillJoined) {
          try { await connection.invoke('JoinThread', id); joinedThreads.add(id); } catch {}
        }
      });
    }
    if (connection.state === signalR.HubConnectionState.Disconnected) {
      try {
        await connection.start();
      } catch (err) {
        // SignalR couldn't connect (no token, network blocked, hub down).
        // Don't throw — the chat UI degrades to REST + polling.
        console.warn('SignalR connect failed:', err?.message || err);
      }
    }
    connectPromise = null;
    return connection;
  })();
  return connectPromise;
}

export function onMessageReceived(handler) {
  messageHandlers.add(handler);
  ensureConnection();
  return () => messageHandlers.delete(handler);
}

export function onThreadBumped(handler) {
  threadBumpHandlers.add(handler);
  ensureConnection();
  return () => threadBumpHandlers.delete(handler);
}

/** Subscribe to seen receipts. Handler receives {threadId, userId, at}; your own opens echo too. */
export function onThreadRead(handler) {
  threadReadHandlers.add(handler);
  ensureConnection();
  return () => threadReadHandlers.delete(handler);
}

/** Subscribe to plan suggestions. Handler receives the `PlanDraftDto` for one of the viewer's chats. */
export function onPlanSuggested(handler) {
  planHandlers.add(handler);
  ensureConnection();
  return () => planHandlers.delete(handler);
}

/** Calls: an incoming invite `{ callId, threadId, video, from: { userId, displayName, avatarUrl }, peers, sentAt }`. */
export function onCallInvite(handler) {
  callInviteHandlers.add(handler);
  ensureConnection();
  return () => callInviteHandlers.delete(handler);
}

/** Calls: one signal `{ callId, threadId, fromUserId, type, payload, at }`. */
export function onCallSignal(handler) {
  callSignalHandlers.add(handler);
  ensureConnection();
  return () => callSignalHandlers.delete(handler);
}

/**
 * Invoke a hub method, waiting (up to ~8 s) for the socket to be up. An idle tab's WebSocket
 * drops and reconnects on a backoff, so "not connected this instant" is the normal state to
 * find it in when someone taps CALL; a call that gave up there read as "couldn't reach them".
 */
async function invokeConnected(method, ...args) {
  const deadline = Date.now() + 8000;
  let conn = await ensureConnection();
  while (conn && conn.state !== signalR.HubConnectionState.Connected && Date.now() < deadline) {
    if (conn.state === signalR.HubConnectionState.Disconnected) { await ensureConnection(); }
    else await new Promise((r) => setTimeout(r, 200));
    conn = connection;
  }
  if (!conn || conn.state !== signalR.HubConnectionState.Connected) throw new Error('Not connected to the server');
  return conn.invoke(method, ...args);
}

/** Ring people: the whole call on the first invite, the newcomers on an ADD. */
export function sendCallInvite(callId, threadId, video, toUserIds, peers) {
  return invokeConnected('CallInvite', callId, threadId, !!video, toUserIds, peers);
}

/** One signal to one person on the call. `payload` is stringified here (SDP, candidates, nothing). */
export function sendCallSignal(callId, threadId, toUserId, type, payload = null) {
  return invokeConnected('CallSignal', callId, threadId, toUserId, type, payload == null ? null : JSON.stringify(payload));
}

/** The caller gave up: stop ringing everyone. */
export function endCallInvite(callId) {
  return invokeConnected('CallEnd', callId).catch(() => {});
}

/**
 * Subscribe to typing events. Handler receives {threadId, userId, at}.
 * Caller is responsible for filtering by active thread + ignoring own
 * userId (the hub broadcasts to OthersInGroup, but multi-tab on the same
 * account would still echo back to your other tabs).
 */
export function onTyping(handler) {
  typingHandlers.add(handler);
  ensureConnection();
  return () => typingHandlers.delete(handler);
}

/**
 * Throttled "I'm typing" ping. Safe to call on every keystroke — we only
 * actually fire once per TYPING_THROTTLE_MS per thread. Silently drops if
 * the connection isn't up (typing is a nice-to-have, not load-bearing).
 */
export async function notifyTyping(threadId) {
  if (!threadId) return;
  const now = Date.now();
  const last = lastTypingSentAt.get(threadId) || 0;
  if (now - last < TYPING_THROTTLE_MS) return;
  lastTypingSentAt.set(threadId, now);
  if (!connection || connection.state !== signalR.HubConnectionState.Connected) return;
  try {
    await connection.invoke('NotifyTyping', threadId);
  } catch {
    // best-effort
  }
}

export async function joinThread(threadId) {
  if (!threadId) return;
  const conn = await ensureConnection();
  if (!conn || conn.state !== signalR.HubConnectionState.Connected) return;
  if (joinedThreads.has(threadId)) return;
  try {
    await conn.invoke('JoinThread', threadId);
    joinedThreads.add(threadId);
  } catch (e) {
    console.warn('JoinThread failed', e?.message || e);
  }
}

export async function leaveThread(threadId) {
  if (!threadId || !connection) return;
  if (connection.state !== signalR.HubConnectionState.Connected) return;
  try {
    await connection.invoke('LeaveThread', threadId);
    joinedThreads.delete(threadId);
  } catch {}
}

/**
 * Tear down on logout — fired by AuthContext.logout so the next user's
 * connection isn't accidentally bound to the previous user's JWT.
 */
export async function disconnectMessagesRealtime() {
  joinedThreads.clear();
  messageHandlers.clear();
  threadBumpHandlers.clear();
  typingHandlers.clear();
  planHandlers.clear();
  callInviteHandlers.clear();
  callSignalHandlers.clear();
  lastTypingSentAt.clear();
  if (connection) {
    try { await connection.stop(); } catch {}
    connection = null;
  }
  connectPromise = null;
}
